// ---------------------------------------------------------------------------
// The till's own storage, for when there is no connection.
//
// IndexedDB rather than localStorage, for one reason that matters: localStorage
// is synchronous and capped at a few megabytes, and a shop's product list plus
// a day of queued sales will not reliably fit. It is also written on the main
// thread, which on a slow machine means the POS stutters while a cashier is
// mid-order.
//
// Five stores, each answering a different question:
//
//   pendingSales - sales rung up with no connection. THIS IS MONEY. Nothing
//                  is ever deleted from it except after the server has
//                  confirmed, by id, that it holds that sale. A sale carries
//                  its payments INSIDE its payload, so the two can never be
//                  queued apart or synced apart.
//   heldOps      - things done to a held cart with no connection: put on the
//                  spike, taken off it, cancelled. Ops rather than snapshots,
//                  because two tills editing the same cart must be
//                  detectable, and a snapshot silently wins.
//   printHistory - every print this till has attempted, and what became of
//                  it. Local, because the record of "did that ticket come
//                  out" has to survive the server being unreachable - which
//                  is exactly when somebody asks.
//   catalogue    - the product list, prices and exchange rate, captured while
//                  the connection was good.
//   meta         - this device's identity and the last sync result.
//
// Written with the raw IndexedDB API and no wrapper library. The surface used
// here is small, and a dependency in the till's critical path is a dependency
// that has to be trusted on the day the connection fails.
// ---------------------------------------------------------------------------

import type { ReceiptConfig } from '../types/design';

const DB_NAME = 'yaghi-pos';
/// Version 2 added `heldOps` and `printHistory`. The upgrade is additive: it
/// creates the missing stores and touches neither the queued sales nor the
/// catalogue, so a till that had money waiting keeps it across the upgrade.
const DB_VERSION = 2;

export const STORE_PENDING = 'pendingSales';
export const STORE_HELD_OPS = 'heldOps';
export const STORE_PRINTS = 'printHistory';
export const STORE_CATALOGUE = 'catalogue';
export const STORE_META = 'meta';

/// Where a queued record has got to.
///
/// `pending`  waiting for a connection.
/// `syncing`  handed to the server, no answer yet. The dangerous state: it
///            may have landed. Nothing is ever discarded from here.
/// `synced`   the server confirmed it. Safe to forget.
/// `failed`   the server refused it. KEPT, with the reason, for a person.
export type SyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';

/// What every queued record carries, whatever it is.
export interface SyncMeta {
  syncStatus: SyncStatus;
  /// How many times sending this has been attempted.
  attempts: number;
  /// Why the server refused it, in a sentence a cashier can act on.
  failedReason?: string;
  lastAttemptAt?: string;
}

/// A sale waiting to be handed to the server.
export interface PendingSale {
  /// The idempotency key, generated the moment the cashier finished the sale.
  /// This is what makes synchronising safe to repeat, so it is also the
  /// primary key here - the same sale can never be queued twice.
  offlineId: string;
  /// The full sale payload, exactly as the online endpoint would receive it.
  payload: Record<string, unknown>;
  /// When the cashier completed it, from the device clock.
  createdAt: string;
  /// The shift that was open at that moment.
  shiftId: string;
  /// For showing the queue on screen without unpacking the payload.
  totalUsd: string;
  itemCount: number;
  /// How many times sending this has been attempted.
  attempts: number;
  /// Set when the server refused it. The sale STAYS in the queue; a person
  /// decides what to do. Losing a real sale to tidy up a queue is not a
  /// trade this system makes.
  failedReason?: string;
  lastAttemptAt?: string;
  /// Added in version 2. Absent on rows queued by an older build, which is
  /// why every reader treats a missing value as `pending`.
  syncStatus?: SyncStatus;
}

// ---------------------------------------------------------------------------
// Held carts
// ---------------------------------------------------------------------------

/// What was done to a held cart while there was no connection.
///
/// An OPERATION, not a snapshot of the cart. A snapshot says "the cart is
/// this now" and quietly overwrites whatever the server has; an operation
/// says "I took this cart off the spike, and it was at version 3 when I did"
/// - which the server can refuse if the cart has moved on since.
export interface PendingHeldOp {
  /// This device's own id for the operation. Stable from the moment the
  /// cashier acted, so a retry carries the same one and the server can
  /// recognise it.
  opId: string;
  kind: 'create' | 'restore' | 'cancel';
  /// The cart. For a `create` the device chooses it, so the row the server
  /// writes has the id the till has been using all along.
  heldOrderId: string;
  /// The cart's version as this device last saw it. The server refuses the
  /// operation if the row has moved on - see the sync endpoint.
  baseVersion: number | null;
  /// When the cashier did it, from the device clock.
  updatedAt: string;
  /// The operation's own arguments: the cart for a create, the reason for a
  /// cancel. Shaped exactly as the online endpoint expects.
  payload: Record<string, unknown>;
  /// For showing the queue without unpacking the payload.
  label: string;
  itemCount: number;

  syncStatus: SyncStatus;
  attempts: number;
  failedReason?: string;
  lastAttemptAt?: string;
  /// Set when the server said the cart had already moved on. Kept, shown,
  /// and never applied over the newer data.
  conflict?: string;
  /// What the SERVER holds, recorded beside the local copy so the screen can
  /// show both and let a person choose. Present only with `conflict`.
  serverVersion?: number;
  serverStatus?: string;
}

// ---------------------------------------------------------------------------
// The print history
// ---------------------------------------------------------------------------

/// One attempt to put something on paper, and what became of it.
///
/// `uncertain` is the state that matters. A job sent to a printer over a raw
/// socket that then went quiet MAY have printed - the protocol returns no
/// acknowledgement. Nothing automatic ever reprints one of these, because the
/// cost of a second preparation ticket is a second cut of meat. A person
/// looks at the printer and decides.
export type PrintState =
  | 'pending'
  | 'printed'
  | 'failed'
  | 'uncertain'
  | 'cancelled'
  | 'reprinted';

export interface PrintRecord {
  /// This device's id for the attempt.
  printId: string;
  /// The server's job id, once there was one.
  jobId: string | null;
  documentType: 'CASHIER_RECEIPT' | 'KITCHEN_TICKET' | 'HELD_ORDER_SHEET';
  printerRole: 'CASHIER_RECEIPT' | 'KITCHEN_TICKET';
  state: PrintState;
  /// What it was for, so the history reads as a list of documents rather
  /// than a list of ids.
  reference: string;
  saleId?: string | null;
  orderId?: string | null;
  heldOrderId?: string | null;
  /// True when this attempt was itself a deliberate copy.
  isReprint: boolean;
  createdAt: string;
  settledAt?: string;
  failedReason?: string;
}

export interface CachedCatalogue {
  capturedAt: string;
  categories: unknown[];
  products: unknown[];
  exchangeRate: { usdToLbp: string; effectiveFrom: string } | null;
  settings: Record<string, string>;
  /// The receipt layout in force when the snapshot was taken, so a receipt
  /// printed with the line down looks like every other. Absent on a snapshot
  /// from an older server.
  printConfig?: { receipt: ReceiptConfig; receiptVersionId: string | null } | null;
}

let connection: Promise<IDBDatabase> | null = null;

/// True when this browser can store anything at all.
///
/// Private-browsing modes and some locked-down installs have no IndexedDB. The
/// till must still work online in that case, so every caller checks rather
/// than assuming.
export function offlineStorageAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

function open(): Promise<IDBDatabase> {
  if (connection) return connection;

  connection = new Promise((resolve, reject) => {
    if (!offlineStorageAvailable()) {
      reject(new Error('This browser cannot store anything offline.'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_PENDING)) {
        const store = db.createObjectStore(STORE_PENDING, { keyPath: 'offlineId' });
        // Sales are replayed oldest first, so the queue is read in that order.
        store.createIndex('createdAt', 'createdAt');
      }

      if (!db.objectStoreNames.contains(STORE_HELD_OPS)) {
        const store = db.createObjectStore(STORE_HELD_OPS, { keyPath: 'opId' });
        // Replayed in the order the cashier did them: putting a cart on the
        // spike and then cancelling it must not arrive the other way round.
        store.createIndex('updatedAt', 'updatedAt');
        store.createIndex('heldOrderId', 'heldOrderId');
      }

      if (!db.objectStoreNames.contains(STORE_PRINTS)) {
        const store = db.createObjectStore(STORE_PRINTS, { keyPath: 'printId' });
        store.createIndex('createdAt', 'createdAt');
        store.createIndex('state', 'state');
      }

      if (!db.objectStoreNames.contains(STORE_CATALOGUE)) {
        db.createObjectStore(STORE_CATALOGUE);
      }

      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open the offline store.'));
  });

  return connection;
}

/// Run one transaction and resolve when it has actually COMMITTED.
///
/// Resolving on the request's success rather than the transaction's complete
/// is a classic IndexedDB mistake: the value looks written, the tab closes,
/// and the write is gone. For a store holding sales that is unacceptable.
async function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest | void,
): Promise<T> {
  const db = await open();

  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);

    let result: T;
    const request = work(store);
    if (request) {
      request.onsuccess = () => {
        result = request.result as T;
      };
    }

    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error ?? new Error('The offline store failed.'));
    transaction.onabort = () => reject(transaction.error ?? new Error('The offline store aborted.'));
  });
}

// ---------------------------------------------------------------------------
// The queue
// ---------------------------------------------------------------------------

/// Put a completed sale into the queue.
///
/// `put` rather than `add`, keyed on the offline id: ringing up the same sale
/// twice is impossible, and a retry of the enqueue itself is harmless.
export async function queueSale(sale: PendingSale): Promise<void> {
  await withStore(STORE_PENDING, 'readwrite', (store) => store.put(sale));
}

/// Everything waiting, oldest first.
export async function listPendingSales(): Promise<PendingSale[]> {
  const rows = await withStore<PendingSale[]>(STORE_PENDING, 'readonly', (store) => store.getAll());
  return (rows ?? []).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function countPendingSales(): Promise<number> {
  const count = await withStore<number>(STORE_PENDING, 'readonly', (store) => store.count());
  return count ?? 0;
}

/// Remove sales the server has confirmed it holds.
///
/// Only ever called with ids the server returned as `synced` or `duplicate`.
/// A sale is never dropped because sending it failed.
export async function forgetSales(offlineIds: string[]): Promise<void> {
  if (offlineIds.length === 0) return;

  const db = await open();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_PENDING, 'readwrite');
    const store = transaction.objectStore(STORE_PENDING);

    for (const id of offlineIds) store.delete(id);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

/// Record that an attempt failed, keeping the sale.
export async function markSaleFailed(offlineId: string, reason: string): Promise<void> {
  const existing = await withStore<PendingSale | undefined>(STORE_PENDING, 'readonly', (store) =>
    store.get(offlineId),
  );

  if (!existing) return;

  await queueSale({
    ...existing,
    attempts: existing.attempts + 1,
    failedReason: reason,
    lastAttemptAt: new Date().toISOString(),
  });
}

/// Clear a failure so the sale is tried again on the next sweep.
export async function clearSaleFailure(offlineId: string): Promise<void> {
  const existing = await withStore<PendingSale | undefined>(STORE_PENDING, 'readonly', (store) =>
    store.get(offlineId),
  );

  if (!existing) return;

  const { failedReason: _dropped, ...rest } = existing;
  await queueSale(rest);
}

/// Remove one sale by hand.
///
/// The only way a queued sale leaves without the server confirming it, and it
/// exists because a sale can become genuinely unrecordable - its product
/// deleted, its shift gone. The caller is expected to make somebody confirm,
/// and the removal is logged to the console for whoever investigates later.
export async function discardSale(offlineId: string): Promise<void> {
  await forgetSales([offlineId]);
}

// ---------------------------------------------------------------------------
// The held-cart queue
// ---------------------------------------------------------------------------

/// Record something done to a held cart with no connection.
///
/// `put` keyed on the operation id, so re-recording the same act is harmless.
export async function queueHeldOp(op: PendingHeldOp): Promise<void> {
  await withStore(STORE_HELD_OPS, 'readwrite', (store) => store.put(op));
}

/// Everything waiting, in the order the cashier did it.
export async function listHeldOps(): Promise<PendingHeldOp[]> {
  const rows = await withStore<PendingHeldOp[]>(STORE_HELD_OPS, 'readonly', (store) =>
    store.getAll(),
  );
  return (rows ?? []).sort((a, b) => a.updatedAt.localeCompare(b.updatedAt));
}

export async function countHeldOps(): Promise<number> {
  const count = await withStore<number>(STORE_HELD_OPS, 'readonly', (store) => store.count());
  return count ?? 0;
}

/// Drop operations the server has confirmed.
export async function forgetHeldOps(opIds: string[]): Promise<void> {
  if (opIds.length === 0) return;

  const db = await open();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_HELD_OPS, 'readwrite');
    const store = transaction.objectStore(STORE_HELD_OPS);
    for (const id of opIds) store.delete(id);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

/// Change one operation's state, keeping everything else about it.
export async function updateHeldOp(
  opId: string,
  patch: Partial<PendingHeldOp>,
): Promise<void> {
  const existing = await withStore<PendingHeldOp | undefined>(STORE_HELD_OPS, 'readonly', (store) =>
    store.get(opId),
  );
  if (!existing) return;

  await queueHeldOp({ ...existing, ...patch });
}

// ---------------------------------------------------------------------------
// The print history
// ---------------------------------------------------------------------------

/// Write down that a print was attempted.
export async function recordPrint(record: PrintRecord): Promise<void> {
  await withStore(STORE_PRINTS, 'readwrite', (store) => store.put(record));
}

export async function updatePrint(printId: string, patch: Partial<PrintRecord>): Promise<void> {
  const existing = await withStore<PrintRecord | undefined>(STORE_PRINTS, 'readonly', (store) =>
    store.get(printId),
  );
  if (!existing) return;

  await recordPrint({ ...existing, ...patch });
}

/// The most recent attempts, newest first.
///
/// Capped rather than unbounded: this is a working record for "did that come
/// out", not an archive. The server's print_jobs table is the archive.
export async function listPrints(limit = 100): Promise<PrintRecord[]> {
  const rows = await withStore<PrintRecord[]>(STORE_PRINTS, 'readonly', (store) => store.getAll());
  return (rows ?? []).sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, limit);
}

/// Forget prints older than the cap, oldest first.
export async function trimPrintHistory(keep = 200): Promise<void> {
  const rows = await listPrints(Number.MAX_SAFE_INTEGER);
  if (rows.length <= keep) return;

  const doomed = rows.slice(keep).map((row) => row.printId);
  const db = await open();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_PRINTS, 'readwrite');
    const store = transaction.objectStore(STORE_PRINTS);
    for (const id of doomed) store.delete(id);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

// ---------------------------------------------------------------------------
// The cached catalogue
// ---------------------------------------------------------------------------

const CATALOGUE_KEY = 'current';

export async function saveCatalogue(catalogue: CachedCatalogue): Promise<void> {
  await withStore(STORE_CATALOGUE, 'readwrite', (store) => store.put(catalogue, CATALOGUE_KEY));
}

export async function readCatalogue(): Promise<CachedCatalogue | null> {
  const value = await withStore<CachedCatalogue | undefined>(STORE_CATALOGUE, 'readonly', (store) =>
    store.get(CATALOGUE_KEY),
  );
  return value ?? null;
}

// ---------------------------------------------------------------------------
// This device
// ---------------------------------------------------------------------------

const DEVICE_KEY = 'deviceId';

/// A stable id for this browser on this machine.
///
/// Used only to make offline ids readable and to label the audit entry, never
/// for authentication - the session cookie does that. A device id that could
/// be presented as proof of anything would be a device id worth stealing.
export async function deviceId(): Promise<string> {
  const existing = await withStore<string | undefined>(STORE_META, 'readonly', (store) =>
    store.get(DEVICE_KEY),
  );

  if (existing) return existing;

  const fresh = `till-${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-4)}`;
  await withStore(STORE_META, 'readwrite', (store) => store.put(fresh, DEVICE_KEY));
  return fresh;
}

// ---------------------------------------------------------------------------
// Receipt numbers reserved for this till
// ---------------------------------------------------------------------------

const RESERVATIONS_KEY = 'receiptReservations';

/// The pool of receipt numbers the server reserved for this till, replaced
/// wholesale by each top-up: the server answers with everything it still
/// holds unused for the device, so the answer IS the pool.
export async function saveReceiptReservations(numbers: string[]): Promise<void> {
  await withStore(STORE_META, 'readwrite', (store) => store.put(numbers, RESERVATIONS_KEY));
}

export async function listReceiptReservations(): Promise<string[]> {
  const value = await withStore<string[] | undefined>(STORE_META, 'readonly', (store) => store.get(RESERVATIONS_KEY));
  return value ?? [];
}

/// Spend the next reserved number, or null when the pool is empty.
///
/// Read and written in ONE transaction, so two sales finishing in the same
/// instant cannot both be handed the same number.
export async function takeReservedReceipt(): Promise<string | null> {
  const db = await open();
  return new Promise<string | null>((resolve, reject) => {
    const transaction = db.transaction(STORE_META, 'readwrite');
    const store = transaction.objectStore(STORE_META);
    const request = store.get(RESERVATIONS_KEY);
    let taken: string | null = null;
    request.onsuccess = () => {
      const pool = (request.result as string[] | undefined) ?? [];
      taken = pool[0] ?? null;
      if (taken) store.put(pool.slice(1), RESERVATIONS_KEY);
    };
    transaction.oncomplete = () => resolve(taken);
    transaction.onerror = () => reject(transaction.error ?? new Error('The offline store failed.'));
    transaction.onabort = () => reject(transaction.error ?? new Error('The offline store aborted.'));
  });
}

/// The id carried by one offline sale.
///
/// Generated when the cashier finishes the sale, never when it is sent. That
/// is the whole basis of duplicate protection: whatever happens to the
/// connection afterwards, every attempt to record this sale carries this
/// string, and the database will only accept it once.
export function newOfflineId(device: string): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 16)
      : `${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`;

  // Kept under the server's 80-character limit with room to spare.
  return `off-${device}-${Date.now().toString(36)}-${random}`;
}

/// Wipe everything. Used when signing out on a shared machine.
///
/// Refuses while sales are still queued: signing out must never be the thing
/// that loses a day's takings.
export async function clearOfflineData(): Promise<{ cleared: boolean; pending: number }> {
  // Sales AND held-cart operations. Both are work the server has not been
  // told about; signing out must never be the thing that loses either.
  const pending = (await countPendingSales()) + (await countHeldOps());
  if (pending > 0) return { cleared: false, pending };

  const db = await open();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction([STORE_CATALOGUE, STORE_META], 'readwrite');
    transaction.objectStore(STORE_CATALOGUE).clear();
    transaction.objectStore(STORE_META).clear();
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });

  return { cleared: true, pending: 0 };
}

/// Only for tests, which need a clean database between cases.
export function resetConnectionForTests(): void {
  connection = null;
}
